import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import KeyTermsSection from "./components/KeyTermsSection";
import StructureSection from "./components/StructureSection";
import ReturnsSection from "./components/ReturnsSection";
import UseOfFundsSection from "./components/UseOfFundsSection";
import TaxConsiderationsSection from "./components/TaxConsiderationsSection";
import RiskSection from "./components/RiskSection";
import FAQSection from "./components/FAQSection";

export default function SocialHousingPage() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    investmentAmount: "",
    investorType: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.investorType) {
      setError("Please fill in all required fields.");
      return;
    }
    if (form.message.length > 500) {
      setError("Message must be 500 characters or fewer.");
      return;
    }
    setError("");
    setSubmitting(true);
    const body = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d73vkr6csbv12mkuct2g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#EEF2F9]">
      <Navbar />

      {/* Hero */}
      <section
        id="social-housing-hero"
        className="relative w-full overflow-hidden"
        style={{
          minHeight: 520,
          backgroundImage: "url('https://readdy.ai/api/search-image?query=aerial%20drone%20photography%20of%20a%20modern%20well%20planned%20UK%20residential%20property%20investment%20estate%2C%20rows%20of%20clean%20brick%20houses%20with%20gardens%20and%20quiet%20streets%2C%20golden%20hour%20warm%20amber%20light%20casting%20long%20shadows%20across%20rooftops%2C%20Yorkshire%20England%20suburban%20development%2C%20no%20people%2C%20wide%20cinematic%20shot%2C%20professional%20architectural%20real%20estate%20investment%20photography%2C%20property%20portfolio%20asset%2C%20tidy%20well%20maintained%20neighbourhood&width=1400&height=520&seq=804&orientation=landscape')",
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Overlays */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(7,26,46,0.92) 0%, rgba(7,26,46,0.78) 50%, rgba(7,26,46,0.60) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(7,26,46,0.65) 0%, transparent 55%)" }} />
        {/* Gold left accent line */}
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "linear-gradient(to bottom, transparent 0%, #C9A84C 25%, #E2C27A 55%, #C9A84C 80%, transparent 100%)" }} />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-20 text-white">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/35 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.18em] px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm">
              <i className="ri-shield-check-line text-xs leading-none" />
              Section 21 FSMA 2000 — Regulated Financial Promotion
            </span>
            <h1
              className="text-4xl md:text-5xl font-bold mb-4 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Social Housing
              <br />
              <span style={{ color: "#C9A84C" }}>Structured Product</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base leading-relaxed mb-6 max-w-xl">
              A 5-year, income-generating investment targeting <strong className="text-white">10.00% per annum</strong> — backed by UK assisted living property assets and long-term housing association leases.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { icon: "ri-percent-line", label: "10.00% p.a." },
                { icon: "ri-calendar-2-line", label: "Monthly Income" },
                { icon: "ri-time-line", label: "5-Year Term" },
                { icon: "ri-building-4-line", label: "UK Asset-Backed" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2.5 text-xs font-medium text-white"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center text-[#C9A84C]">
                    <i className={`${item.icon} leading-none`} />
                  </div>
                  {item.label}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#C9A84C] text-[#071A2E] font-semibold text-sm hover:bg-[#B8953B] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-sm leading-none" />
              Schedule a Call with Our Team
            </button>
          </div>
        </div>
      </section>

      {/* FSMA Notice Banner */}
      <section className="bg-[#071A2E] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center text-[#C9A84C] flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-line leading-none" />
            </div>
            <p className="text-slate-300 text-xs leading-relaxed">
              <strong className="text-[#C9A84C]">Important Notice (Section 21 FSMA 2000):</strong> This document constitutes a financial promotion. It is intended solely for Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors. Investments are speculative and involve risk. Investors may lose some or all of their capital. Past performance is not indicative of future results.
            </p>
          </div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-2">
              <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-4">
                Overview
              </span>
              <h2
                className="text-2xl md:text-3xl font-bold text-[#071A2E] mb-5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Executive Summary
              </h2>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                The Thorpe Dellow Social Housing Structured Product is a 5-year, income-generating investment designed to provide monthly distributions backed by UK social housing assets. The investment targets a return of <strong className="text-[#071A2E]">10.00% per annum</strong>, derived from long-term lease agreements with a UK-based housing association operating assisted living properties.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed">
                At maturity, investors may elect to receive full capital repayment or roll over into a subsequent term. Investor capital is secured by first legal charges over all underlying properties, held by an Independent Security Trustee.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Target Return", value: "10.00%", sub: "Per annum" },
                { label: "Income", value: "Monthly", sub: "Pro rata distributions" },
                { label: "Term", value: "5 Years", sub: "With rollover option" },
                { label: "Asset Class", value: "UK Social Housing", sub: "Assisted living" },
              ].map((s) => (
                <div key={s.label} className="bg-[#EEF2F9] rounded-2xl p-5 text-center">
                  <p
                    className="font-bold text-[#C9A84C] text-lg mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {s.value}
                  </p>
                  <p className="text-[#071A2E] text-xs font-semibold">{s.label}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <KeyTermsSection />
      <StructureSection />
      <ReturnsSection />
      <UseOfFundsSection />
      <TaxConsiderationsSection />
      <RiskSection />
      <FAQSection />

      {/* Expression of Interest Form */}
      <section className="bg-white py-20 md:py-24" id="apply">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
              Get Started
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#071A2E]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Expression of Interest
            </h2>
            <p className="text-slate-500 text-sm mt-3">
              Complete the form below and our investment team will contact you within 2 business days. Please ensure you have read and understood the full Information Memorandum before proceeding.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-16 bg-[#EEF2F9] rounded-2xl">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                <i className="ri-check-line text-green-600 text-2xl leading-none" />
              </div>
              <h3 className="font-bold text-[#071A2E] text-xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Expression of Interest Received
              </h3>
              <p className="text-slate-500 text-sm max-w-sm mx-auto">
                Thank you. Our investment team will review your submission and contact you within 2 business days.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-[#EEF2F9] rounded-2xl p-8 flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    required
                    placeholder="Jane Smith"
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="jane@example.com"
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+44 (0)7700 000000"
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Indicative Investment Amount</label>
                  <select
                    name="investmentAmount"
                    value={form.investmentAmount}
                    onChange={handleChange}
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors cursor-pointer"
                  >
                    <option value="">Select amount</option>
                    <option>£10,000 – £25,000</option>
                    <option>£25,000 – £50,000</option>
                    <option>£50,000 – £100,000</option>
                    <option>£100,000 – £250,000</option>
                    <option>£250,000+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Investor Classification *</label>
                <select
                  name="investorType"
                  value={form.investorType}
                  onChange={handleChange}
                  required
                  className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors cursor-pointer"
                >
                  <option value="">Select classification</option>
                  <option>Certified High Net Worth Individual</option>
                  <option>Self-Certified Sophisticated Investor</option>
                  <option>Retail Investor (subject to appropriateness assessment)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">
                  Additional Comments
                  <span className="font-normal text-slate-400 ml-1">(max 500 characters)</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder="Any questions or additional information you would like to share..."
                  className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors resize-none"
                />
                <p className="text-right text-xs text-slate-400 mt-1">{form.message.length}/500</p>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                <p className="text-xs text-slate-500 leading-relaxed">
                  By submitting this form, you confirm you have read the full Information Memorandum and understand that this is a speculative investment. Capital may be at risk. We recommend seeking independent financial advice.
                </p>
              </div>

              {error && (
                <p className="text-red-500 text-xs bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-xl bg-[#071A2E] text-white font-semibold text-sm hover:bg-[#0D2B4A] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
              >
                {submitting ? "Submitting..." : "Submit Expression of Interest"}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
